import { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { MUNICIPIO_SIADAP_ID } from '@/lib/municipios';

export type AvaliacaoSiadap = 'Excelente' | 'Muito Bom' | 'Bom' | 'Regular' | 'Inadequado' | 'Adequado' | 'Relevante';

export interface RegistoSiadap {
  id: string;
  funcionarioId: string;
  periodoAvaliacao: string;
  avaliacao: AvaliacaoSiadap;
  pontosChefe: number;
  pontosCCA: number;
  descricaoSubida: string;
  nivel: number;
  remuneracaoAtual: number;
}

export interface RegistoHistoricoProgressao {
  id: string;
  funcionarioId: string;
  nomeFuncionario: string;
  carreira: string;
  ano: number;
  pontuacaoChefe: number;
  pontuacaoCCA: number;
  classificacaoServico: string;
}

export interface ProgressaoRegistada {
  id: string;
  funcionarioId: string;
  dataProgressao: string;
  nivelAnterior: number;
  nivelNovo: number;
  pontosDescontados: number;
  observacoes: string | null;
  aceleradorRemuneratorio: boolean;
}

interface SiadapContextValue {
  registosSiadap: RegistoSiadap[];
  registosHistorico: RegistoHistoricoProgressao[];
  progressoes: ProgressaoRegistada[];
  /** Soma real dos pontos descontados por funcionário (fonte: tabela progressoes) */
  pontosDescontadosPorFuncionario: Record<string, number>;
  loadingSiadap: boolean;
  reloadSiadap: () => Promise<void>;
  /** Contagem de avaliações (SIADAP + histórico) por funcionário */
  avaliacoesPorFuncionario: Record<string, number>;
}

const SiadapContext = createContext<SiadapContextValue | null>(null);

function toRegistoSiadap(row: Record<string, unknown>): RegistoSiadap {
  return {
    id: row.id as string,
    funcionarioId: row.funcionario_id as string,
    periodoAvaliacao: row.periodo_avaliacao as string,
    avaliacao: row.avaliacao as AvaliacaoSiadap,
    pontosChefe: Number(row.pontos_chefe),
    pontosCCA: Number(row.pontos_cca),
    descricaoSubida: (row.descricao_subida as string) ?? '',
    nivel: row.nivel as number,
    remuneracaoAtual: Number(row.remuneracao_atual),
  };
}

function toRegistoHistorico(row: Record<string, unknown>): RegistoHistoricoProgressao {
  return {
    id: row.id as string,
    funcionarioId: row.funcionario_id as string,
    nomeFuncionario: (row.nome_funcionario as string) ?? '',
    carreira: (row.carreira as string) ?? '',
    ano: row.ano as number,
    pontuacaoChefe: Number(row.pontuacao_chefe),
    pontuacaoCCA: Number(row.pontuacao_cca),
    classificacaoServico: (row.classificacao_servico as string) ?? '',
  };
}

export function SiadapProvider({ children }: { children: ReactNode }) {
  const [registosSiadap, setRegistosSiadap] = useState<RegistoSiadap[]>([]);
  const [registosHistorico, setRegistosHistorico] = useState<RegistoHistoricoProgressao[]>([]);
  const [progressoes, setProgressoes] = useState<ProgressaoRegistada[]>([]);
  const [loadingSiadap, setLoadingSiadap] = useState(true);
  // Evita re-fetch em navegações — dados ficam em memória durante a sessão
  const loadedRef = useRef(false);

  // SIADAP usa sempre o município fixo
  const municipioId = MUNICIPIO_SIADAP_ID;

  const reloadSiadap = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      setLoadingSiadap(false);
      return;
    }
    // Permite re-fetch manual (ex: após registar progressão)
    loadedRef.current = true;
    setRegistosSiadap([]);
    setRegistosHistorico([]);
    setProgressoes([]);
    setLoadingSiadap(true);
    try {
      const [{ data: siadap }, { data: historico }, { data: progs }] = await Promise.all([
        supabase.from('registos_siadap').select('*').eq('municipio_id', municipioId).order('created_at'),
        supabase.from('historico_progressoes').select('*').eq('municipio_id', municipioId).order('ano', { ascending: false }),
        supabase.from('progressoes').select('*').eq('municipio_id', municipioId).order('data_progressao', { ascending: true }),
      ]);
      setRegistosSiadap((siadap ?? []).map(toRegistoSiadap));
      setRegistosHistorico((historico ?? []).map(toRegistoHistorico));
      setProgressoes(
        (progs ?? []).map((row) => ({
          id: row.id as string,
          funcionarioId: row.funcionario_id as string,
          dataProgressao: row.data_progressao as string,
          nivelAnterior: row.nivel_anterior as number,
          nivelNovo: row.nivel_novo as number,
          pontosDescontados: Number(row.pontos_descontados),
          observacoes: row.observacoes as string | null,
          aceleradorRemuneratorio: !!row.acelerador_remuneratorio,
        }))
      );
    } catch (e: unknown) {
      console.error('[SiadapContext] Erro ao carregar dados:', e);
    } finally {
      setLoadingSiadap(false);
    }
  }, [municipioId]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION')) {
        // Só carrega uma vez por sessão — navegações não re-fetcham
        if (loadedRef.current) return;
        loadedRef.current = true;
        reloadSiadap();
      } else if (!session) {
        setRegistosSiadap([]);
        setRegistosHistorico([]);
        setProgressoes([]);
        setLoadingSiadap(false);
        loadedRef.current = false;
      }
    });
    return () => subscription.unsubscribe();
  }, [reloadSiadap]);

  const avaliacoesPorFuncionario: Record<string, number> = {};
  registosSiadap.forEach((r) => {
    avaliacoesPorFuncionario[r.funcionarioId] = (avaliacoesPorFuncionario[r.funcionarioId] ?? 0) + 1;
  });
  registosHistorico.forEach((r) => {
    avaliacoesPorFuncionario[r.funcionarioId] = (avaliacoesPorFuncionario[r.funcionarioId] ?? 0) + 1;
  });

  // Soma real dos pontos descontados por funcionário — fonte de verdade
  const pontosDescontadosPorFuncionario: Record<string, number> = {};
  progressoes.forEach((p) => {
    pontosDescontadosPorFuncionario[p.funcionarioId] =
      (pontosDescontadosPorFuncionario[p.funcionarioId] ?? 0) + p.pontosDescontados;
  });

  return (
    <SiadapContext.Provider value={{
      registosSiadap,
      registosHistorico,
      progressoes,
      pontosDescontadosPorFuncionario,
      loadingSiadap,
      reloadSiadap,
      avaliacoesPorFuncionario,
    }}>
      {children}
    </SiadapContext.Provider>
  );
}

export function useSiadap(): SiadapContextValue {
  const ctx = useContext(SiadapContext);
  if (!ctx) throw new Error('useSiadap must be used within SiadapProvider');
  return ctx;
}
